import React from "react";
import "./ResizeControlMenu.css";
import { getResizePresets } from "../../../../../services/api/catalog/resize_presets";
import { PASSPORT_CATEGORY_ID } from "../../../../../utils/passportPresetUtils";
import { parsePresetPixelSize } from "../../../../../utils/getTransform";
import ResizePresetIcon, {
  formatPresetDims,
  formatPresetMetaLine,
} from "./ResizePresetIcon";
import {
  addRecentResizePreset,
  isCatalogCustomTile,
  isCustomResizePreset,
} from "./customResizePreset";
import "./ResizePresetIcon.css";

const dimensionsForPreset = (preset) =>
  formatPresetDims(preset) || preset?.dimensions || "";

const metaLabelForPreset = (preset) => formatPresetMetaLine(preset);

const brandNameForPreset = (preset) =>
  preset?.brand_name || "";

const isStandardPreset = (preset) =>
  preset?.category_id === 1 ||
  String(preset?.brand_slug || "").toLowerCase() === "standard" ||
  String(preset?.brand_name || "").toLowerCase() === "standard";

function readRecentPresets() {
  try {
    const saved = localStorage.getItem("recentPresets");
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

/** Shared search field used by editor Resize panel + prep popup sticky headers. */
export const ResizePresetSearch = ({
  value = "",
  onChange = () => {},
  className = "",
}) => (
  <div className={`resize-search-box${className ? ` ${className}` : ""}`}>
    <input
      type="text"
      placeholder="Search Size Presets"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="resize-search-input"
    />
    <span className="resize-search-icon">🔍</span>
  </div>
);

const ResizeControlMenu = ({
  onResizeSelect = () => {},
  onCustomPresetActivate = null,
  selectedPresetId = null,
  selectedPreset = null,
  customFlyoutOpen = false,
  searchTerm: searchTermProp = null,
  hideSearch = false,
}) => {
  const [searchTermInternal, setSearchTermInternal] = React.useState("");
  const isSearchControlled = searchTermProp != null;
  const searchTerm = isSearchControlled ? searchTermProp : searchTermInternal;
  const [categoriesFromApi, setCategoriesFromApi] = React.useState(null);
  const [presetsLoading, setPresetsLoading] = React.useState(true);
  const [recentPresets, setRecentPresets] = React.useState(readRecentPresets);

  React.useEffect(() => {
    let cancelled = false;
    getResizePresets({ excludeCategoryIds: [PASSPORT_CATEGORY_ID] })
      .then((res) => {
        if (!cancelled && res?.success && Array.isArray(res.data)) {
          setCategoriesFromApi(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) setCategoriesFromApi(null);
      })
      .finally(() => {
        if (!cancelled) setPresetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const sync = () => setRecentPresets(readRecentPresets());
    window.addEventListener("resize-recent-presets-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("resize-recent-presets-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const addToRecent = (preset) => {
    addRecentResizePreset(preset);
    setRecentPresets(readRecentPresets());
  };

  const removeFromRecent = (index, e) => {
    e.stopPropagation();
    setRecentPresets((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      try {
        localStorage.setItem("recentPresets", JSON.stringify(updated));
      } catch {
        // ignore
      }
      return updated;
    });
  };

  const clearRecent = (e) => {
    e.stopPropagation();
    setRecentPresets([]);
    try {
      localStorage.removeItem("recentPresets");
    } catch {
      // ignore
    }
  };

  const handlePresetClick = (preset, event) => {
    // Catalog "Custom" opens a separate Custom Size panel (does not apply 0×0).
    if (isCatalogCustomTile(preset)) {
      if (typeof onCustomPresetActivate === "function") {
        const anchorEl =
          event?.currentTarget ||
          (event?.target instanceof Element
            ? event.target.closest(".resize-aspect-card")
            : null);
        onCustomPresetActivate({ preset, anchorEl });
      }
      return;
    }
    const dimensions = dimensionsForPreset(preset);
    const forEditor = { ...preset, dimensions };
    addToRecent(preset);
    onResizeSelect(forEditor);
  };

  const selectedItemRef = React.useRef(null);

  React.useEffect(() => {
    if ((!selectedPresetId && !selectedPreset) || !selectedItemRef.current) return;
    selectedItemRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [selectedPresetId, selectedPreset, categoriesFromApi, presetsLoading]);

  const resolvedCustomSelected =
    Boolean(selectedPreset) &&
    isCustomResizePreset(selectedPreset) &&
    Boolean(parsePresetPixelSize(selectedPreset));

  const isSelectedPreset = (preset) => {
    // Highlight Custom while the separate Custom Size panel is open (1st-image UX).
    if (isCatalogCustomTile(preset) && customFlyoutOpen) return true;
    if (isCatalogCustomTile(preset) && resolvedCustomSelected) return true;
    if (selectedPresetId != null && preset?.id === selectedPresetId) return true;
    if (selectedPreset) {
      return (
        preset?.name === selectedPreset.name &&
        dimensionsForPreset(preset) === dimensionsForPreset(selectedPreset)
      );
    }
    return false;
  };

  const filterPresets = (presets) => {
    if (!searchTerm) return presets;
    const q = searchTerm.toLowerCase();
    return presets.filter((preset) => {
      const hay = [
        preset.name,
        preset.brand_name,
        preset.label,
        preset.type_name,
        dimensionsForPreset(preset),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  };

  const renderPresetCard = (preset, { key, recentIndex = null, showBrandMark = false } = {}) => {
    const selected = isSelectedPreset(preset);
    const isRecent = recentIndex != null;
    const displayPreset =
      isCatalogCustomTile(preset) && resolvedCustomSelected
        ? selectedPreset
        : preset;
    // Hide catalog Custom's placeholder "0 × 0" until a real size is applied.
    const showResolvedCustomDims =
      isCatalogCustomTile(preset) && resolvedCustomSelected;
    const dims =
      isCatalogCustomTile(preset) && !showResolvedCustomDims
        ? ""
        : dimensionsForPreset(displayPreset);
    const metaLabel =
      isCatalogCustomTile(preset) && !showResolvedCustomDims
        ? metaLabelForPreset(preset) || "Custom"
        : metaLabelForPreset(displayPreset);
    const brandName = brandNameForPreset(preset);
    const showLogo =
      showBrandMark &&
      !isStandardPreset(preset) &&
      Boolean(preset.icon_url);
    return (
      <div
        key={key}
        ref={selected ? selectedItemRef : null}
        className={`resize-aspect-card${selected ? " is-selected" : ""}${
          isRecent ? " resize-aspect-card--recent" : ""
        }`}
        onClick={(e) => handlePresetClick(preset, e)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handlePresetClick(preset, e);
        }}
      >
        {isRecent ? (
          <button
            type="button"
            className="resize-aspect-card__remove"
            onClick={(e) => removeFromRecent(recentIndex, e)}
            title="Remove from recently used"
          >
            ✕
          </button>
        ) : null}
        <ResizePresetIcon
          width={displayPreset.width}
          height={displayPreset.height}
          aspectRatio={displayPreset.aspect_ratio}
          size={36}
        />
        <div className="resize-aspect-card__text">
          {dims ? (
            <p className="resize-aspect-card__dims" title={dims}>
              {dims}
            </p>
          ) : null}
          {metaLabel ? (
            <p className="resize-aspect-card__meta-label" title={metaLabel}>
              {metaLabel}
            </p>
          ) : null}
          {brandName && !isStandardPreset(preset) ? (
            <p className="resize-aspect-card__brand" title={brandName}>
              {brandName}
            </p>
          ) : null}
          {showLogo ? (
            <img
              src={preset.icon_url}
              alt=""
              className="resize-aspect-card__brand-logo"
              draggable={false}
            />
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="resize-control-menu-container">
      {!hideSearch ? (
        <ResizePresetSearch
          value={searchTerm}
          onChange={isSearchControlled ? () => {} : setSearchTermInternal}
        />
      ) : null}

      <div className="resize-section">
        <div className="resize-section-header">
          <h3>Recently Used</h3>
          <button
            type="button"
            className="clear-btn"
            onClick={clearRecent}
            disabled={recentPresets.length === 0}
          >
            Clear All
          </button>
        </div>
        {recentPresets.length === 0 ? (
          <p className="empty-message">No recently used presets</p>
        ) : (
          <div className="resize-aspect-grid" role="listbox" aria-label="Recently used">
            {recentPresets.map((preset, index) =>
              renderPresetCard(preset, {
                key: `recent-${preset.id || preset.name}-${index}`,
                recentIndex: index,
                showBrandMark:
                  !isStandardPreset(preset) &&
                  Boolean(preset.icon_url),
              })
            )}
          </div>
        )}
      </div>

      {presetsLoading ? (
        <div className="resize-section">
          <p className="empty-message">Loading presets…</p>
        </div>
      ) : categoriesFromApi && categoriesFromApi.length > 0 ? (
        categoriesFromApi.map((cat) => {
          const presets = filterPresets(cat.presets || []);
          if (!presets.length && searchTerm) return null;
          const isStandard =
            cat.category_id === 1 ||
            String(cat.category_name || "").toUpperCase() === "STANDARD";
          const showBrandMark = !isStandard;
          const sectionTitle = isStandard ? "Standard" : cat.category_name;
          return (
            <div key={cat.category_id} className="resize-section">
              <h3>{sectionTitle}</h3>
              <div
                className="resize-aspect-grid"
                role="listbox"
                aria-label={sectionTitle}
              >
                {presets.map((preset) =>
                  renderPresetCard(preset, {
                    key: preset.id,
                    showBrandMark:
                      showBrandMark && Boolean(preset.icon_url),
                  })
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className="resize-section">
          <p className="empty-message">Could not load presets. Please try again later.</p>
        </div>
      )}
    </div>
  );
};

export default ResizeControlMenu;
